// components/TermsOfService.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './styles/legalPage.css';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="legal-back">&larr; Back to Home</Link>
        <h1 className="legal-title">Terms of Service</h1>
        <p className="legal-updated">Last Updated: March 11, 2026</p>

        <section className="legal-section">
          <h2>1. Agreement to Terms</h2>
          <p>
            These Terms of Service ("Terms") constitute a legally binding agreement between you
            and Mangold, Inc. ("Company," "we," "us," or "our"). By accessing our website
            mangold.ai, engaging our services, or entering into a service agreement with us,
            you agree to be bound by these Terms. If you do not agree, please do not use our
            services.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Services</h2>
          <p>
            Mangold, Inc. provides digital marketing, web development, mobile app development,
            UI/UX design, SEO, app store optimization (ASO), paid advertising management,
            and related project management services. The specific scope, deliverables, timeline,
            and fees for each engagement will be detailed in a separate Statement of Work (SOW)
            or service agreement.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Client Obligations</h2>
          <p>By engaging our services, you agree to:</p>
          <ul>
            <li>Provide timely access to necessary accounts, platforms, and materials required for project execution</li>
            <li>Designate an authorized point of contact for project communications</li>
            <li>Review and provide feedback on deliverables within agreed-upon timeframes</li>
            <li>Ensure that all content, materials, and data you provide are accurate and do not infringe on any third-party rights</li>
            <li>Maintain the confidentiality of any shared credentials and promptly notify us of any security concerns</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Account Access and Credentials</h2>
          <p>
            In the course of providing services, you may grant us access to third-party accounts
            including but not limited to advertising platforms, analytics tools, content management
            systems, and app store accounts. You retain full ownership of these accounts. We will
            use access solely for the purpose of performing contracted services and will return or
            relinquish access upon termination of the engagement.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Intellectual Property</h2>
          <h3>5.1 Client Materials</h3>
          <p>
            You retain all rights to materials, content, brand assets, and data you provide to us.
            You grant us a limited license to use these materials solely for the purpose of
            performing the contracted services.
          </p>
          <h3>5.2 Deliverables</h3>
          <p>
            Upon full payment, all custom deliverables created specifically for your project
            (including designs, code, and content) will be assigned to you, unless otherwise
            specified in the SOW. We retain the right to use general knowledge, techniques,
            and non-proprietary tools developed during the engagement.
          </p>
          <h3>5.3 Portfolio Rights</h3>
          <p>
            Unless otherwise agreed in writing, we reserve the right to showcase completed
            project work in our portfolio, case studies, and marketing materials.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Payment Terms</h2>
          <ul>
            <li>Payment terms, rates, and schedules will be outlined in the applicable SOW or service agreement</li>
            <li>Invoices are due upon receipt unless otherwise specified</li>
            <li>Late payments may incur interest at the rate of 1.5% per month or the maximum rate permitted by law</li>
            <li>We reserve the right to pause work on any project with outstanding payments exceeding 30 days</li>
            <li>Advertising spend and third-party costs are the responsibility of the client and are separate from our service fees</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Advertising and Marketing Services</h2>
          <p>
            For digital marketing engagements including paid advertising (PPC), SEO, and ASO services:
          </p>
          <ul>
            <li>We do not guarantee specific results, rankings, or return on ad spend, as these depend on many factors beyond our control</li>
            <li>Advertising budgets are managed on your behalf, and you are responsible for all media spend</li>
            <li>We will provide regular performance reports as outlined in the SOW</li>
            <li>Changes to campaign strategy may be recommended based on performance data and market conditions</li>
            <li>Platform policy changes by third parties (Google, Apple, Meta, etc.) may affect campaign performance and strategy</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>8. Confidentiality</h2>
          <p>
            Both parties agree to maintain the confidentiality of proprietary information shared
            during the engagement. This includes business strategies, technical data, marketing
            plans, financial information, and any other information designated as confidential.
            This obligation survives the termination of services for a period of two (2) years.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Mangold, Inc. shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages, including loss of
            profits, data, or business opportunities, arising from or related to our services.
            Our total liability for any claim arising from our services shall not exceed the total
            fees paid by you for the specific service giving rise to the claim in the twelve (12)
            months preceding the event.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Mangold, Inc. and its officers, directors,
            employees, and agents from any claims, damages, or expenses arising from: (a) your
            breach of these Terms; (b) your violation of any third-party rights; (c) content or
            materials you provide that infringe on intellectual property rights; or (d) your use
            of deliverables in a manner not contemplated by the SOW.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Termination</h2>
          <ul>
            <li>Either party may terminate services with 30 days written notice, unless otherwise specified in the SOW</li>
            <li>Upon termination, you are responsible for payment of all work completed up to the termination date</li>
            <li>We will provide reasonable transition assistance, including transferring account access and delivering completed work</li>
            <li>Any pre-paid fees for unperformed services will be refunded on a pro-rata basis</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>12. Warranties and Disclaimers</h2>
          <p>
            We warrant that our services will be performed in a professional and workmanlike manner
            consistent with industry standards. Except as expressly stated herein, all services are
            provided "as is" without warranties of any kind, whether express or implied, including
            but not limited to implied warranties of merchantability or fitness for a particular purpose.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Force Majeure</h2>
          <p>
            Neither party shall be liable for delays or failure to perform resulting from causes
            beyond its reasonable control, including but not limited to acts of God, natural
            disasters, war, terrorism, pandemic, government actions, or failures of third-party
            platforms and services.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the
            State of Delaware, United States, without regard to its conflict of law provisions.
            Any disputes arising under these Terms shall be resolved in the courts located in
            Kent County, Delaware.
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. Material changes will be
            communicated to active clients via email. Continued use of our services after
            changes constitutes acceptance of the updated Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>16. Contact Us</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <p>
            <strong>Mangold, Inc.</strong><br />
            1111b South Governors Avenue, STE 48507<br />
            Dover, DE 19904, USA<br />
            Email: <a href="mailto:info@mangold.ai">info@mangold.ai</a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;
